import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Cookie, X, Settings2 } from "lucide-react";

const STORAGE_KEY = "as_cookie_consent_v1";
const OPEN_EVENT = "open-cookie-preferences";

// Helper: any component can call this to reopen the preferences modal.
export function openCookiePreferences() {
  try { window.dispatchEvent(new Event(OPEN_EVENT)); } catch { /* SSR-safe */ }
}

// Read current saved consent (with silent migration from iter52 shape {choice, at}).
function readConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    // Legacy iter52 shape: {choice: 'accepted'|'rejected'|'dismissed', at}
    if (parsed && typeof parsed.choice === "string" && parsed.analytics === undefined) {
      const accepted = parsed.choice === "accepted";
      return { essential: true, analytics: accepted, marketing: accepted, at: parsed.at };
    }
    if (parsed && typeof parsed === "object") return parsed;
    return null;
  } catch { return null; }
}

function saveConsent(prefs) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...prefs, essential: true, at: new Date().toISOString() }));
  } catch { /* private-mode fallback: keep in-memory only */ }
}

export default function CookieConsent() {
  // 'banner' = first-visit slide-up bar; 'modal' = full preferences panel; null = hidden
  const [mode, setMode] = useState(null);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  const hydrateFromStorage = useCallback(() => {
    const existing = readConsent();
    if (existing) {
      setAnalytics(!!existing.analytics);
      setMarketing(!!existing.marketing);
    }
    return existing;
  }, []);

  useEffect(() => {
    const existing = hydrateFromStorage();
    if (!existing) {
      const t = setTimeout(() => setMode("banner"), 600);
      return () => clearTimeout(t);
    }
  }, [hydrateFromStorage]);

  // Listen for footer "Cookie preferences" clicks
  useEffect(() => {
    const handler = () => {
      hydrateFromStorage();
      setMode("modal");
    };
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, [hydrateFromStorage]);

  const acceptAll = () => {
    saveConsent({ analytics: true, marketing: true });
    setAnalytics(true); setMarketing(true);
    setMode(null);
  };
  const rejectNonEssential = () => {
    saveConsent({ analytics: false, marketing: false });
    setAnalytics(false); setMarketing(false);
    setMode(null);
  };
  const saveCurrent = () => {
    saveConsent({ analytics, marketing });
    setMode(null);
  };

  if (!mode) return null;

  // ============================================================
  // Full preferences modal — opened from footer OR "Customize" on banner
  // ============================================================
  if (mode === "modal") {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Cookie preferences"
        className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm px-4 py-6"
        data-testid="cookie-preferences-modal"
        onClick={(e) => { if (e.target === e.currentTarget) setMode(null); }}
      >
        <div className="bg-[#F0EFEA] text-[#11211A] w-full max-w-lg shadow-2xl border-l-4 border-[#264839] max-h-[92vh] overflow-y-auto">
          <div className="flex items-start justify-between px-6 pt-6 pb-4">
            <div className="flex items-center gap-3">
              <Settings2 className="w-5 h-5 text-[#B35C4A]" aria-hidden="true" />
              <div>
                <div className="tracking-overline text-[10px] text-[#B35C4A]">Your Choices</div>
                <h2 className="font-serif text-2xl leading-tight">Cookie preferences</h2>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setMode(null)}
              className="p-1 opacity-70 hover:opacity-100"
              aria-label="Close"
              data-testid="cookie-preferences-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="px-6 pb-2 text-sm text-[#4A5550] leading-relaxed">
            We use cookies to run the site, understand how it's used, and (optionally) tailor advertising. Choose which categories you're comfortable with — you can change your mind any time from the footer.{" "}
            <Link to="/privacy" className="text-[#264839] underline hover:text-[#11211A]">Read the full Privacy Policy</Link>.
          </div>

          <div className="px-6 py-4 space-y-3">
            {/* Essential — always on, disabled */}
            <label className="flex items-start gap-3 bg-white border border-[#E8ECE9] px-4 py-3 cursor-not-allowed opacity-90" data-testid="cookie-cat-essential">
              <input type="checkbox" checked disabled className="mt-1 w-4 h-4 accent-[#264839]" />
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <div className="font-semibold text-[15px]">Essential</div>
                  <span className="text-[10px] uppercase tracking-widest text-[#264839] bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-sm">Always on</span>
                </div>
                <div className="text-xs text-[#4A5550] mt-1 leading-relaxed">Required for authentication, checkout, and remembering your preferences. Cannot be turned off.</div>
              </div>
            </label>

            {/* Analytics */}
            <label className="flex items-start gap-3 bg-white border border-[#E8ECE9] px-4 py-3 cursor-pointer hover:border-[#264839] transition" data-testid="cookie-cat-analytics">
              <input
                type="checkbox"
                checked={analytics}
                onChange={(e) => setAnalytics(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#264839]"
                data-testid="cookie-cat-analytics-toggle"
              />
              <div className="flex-1">
                <div className="font-semibold text-[15px]">Analytics</div>
                <div className="text-xs text-[#4A5550] mt-1 leading-relaxed">Anonymous traffic and usage data (e.g., which pages are most helpful) so we can improve the directory.</div>
              </div>
            </label>

            {/* Marketing */}
            <label className="flex items-start gap-3 bg-white border border-[#E8ECE9] px-4 py-3 cursor-pointer hover:border-[#264839] transition" data-testid="cookie-cat-marketing">
              <input
                type="checkbox"
                checked={marketing}
                onChange={(e) => setMarketing(e.target.checked)}
                className="mt-1 w-4 h-4 accent-[#264839]"
                data-testid="cookie-cat-marketing-toggle"
              />
              <div className="flex-1">
                <div className="font-semibold text-[15px]">Marketing</div>
                <div className="text-xs text-[#4A5550] mt-1 leading-relaxed">Used to deliver relevant supplier recommendations and measure the impact of campaigns.</div>
              </div>
            </label>
          </div>

          <div className="px-6 py-4 border-t border-[#E8ECE9] flex flex-wrap gap-2 justify-end bg-[#F5F4EF]">
            <button
              type="button"
              onClick={rejectNonEssential}
              className="text-xs uppercase tracking-widest font-semibold px-4 py-2 border border-[#264839] text-[#264839] hover:bg-[#264839]/5 transition"
              data-testid="cookie-preferences-reject"
            >
              Reject non-essential
            </button>
            <button
              type="button"
              onClick={saveCurrent}
              className="text-xs uppercase tracking-widest font-semibold px-4 py-2 border border-[#264839] text-[#264839] hover:bg-[#264839]/5 transition"
              data-testid="cookie-preferences-save"
            >
              Save my choices
            </button>
            <button
              type="button"
              onClick={acceptAll}
              className="text-xs uppercase tracking-widest font-semibold px-4 py-2 bg-[#B35C4A] hover:bg-[#9E4E3E] text-white transition"
              data-testid="cookie-preferences-accept-all"
            >
              Accept all
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  // First-visit slide-up banner
  // ============================================================
  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      className="fixed inset-x-0 bottom-0 z-[100] px-4 sm:px-6 pb-4 pointer-events-none"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto pointer-events-auto bg-[#11211A] text-[#F0EFEA] shadow-2xl border-l-4 border-[#B35C4A] px-5 py-4 sm:py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <Cookie className="w-6 h-6 text-[#B35C4A] flex-shrink-0 hidden sm:block" aria-hidden="true" />
        <div className="text-sm leading-relaxed flex-1">
          <div className="tracking-overline text-[10px] text-[#B35C4A] mb-1">Cookies &amp; Tracking</div>
          <p>
            We use essential cookies to run the site and optional analytics / marketing cookies to improve it. See our{" "}
            <Link to="/privacy" className="underline hover:text-white" data-testid="cookie-consent-privacy-link">Privacy Policy</Link>
            . You can change your choices any time.
          </p>
        </div>
        <div className="flex flex-wrap gap-2 flex-shrink-0 w-full sm:w-auto">
          <button
            type="button"
            onClick={() => setMode("modal")}
            className="text-xs uppercase tracking-widest font-semibold px-4 py-2 border border-[#F0EFEA]/40 hover:bg-white/10 transition"
            data-testid="cookie-consent-customize"
          >
            Customize
          </button>
          <button
            type="button"
            onClick={rejectNonEssential}
            className="text-xs uppercase tracking-widest font-semibold px-4 py-2 border border-[#F0EFEA]/40 hover:bg-white/10 transition"
            data-testid="cookie-consent-reject"
          >
            Reject non-essential
          </button>
          <button
            type="button"
            onClick={acceptAll}
            className="text-xs uppercase tracking-widest font-semibold px-4 py-2 bg-[#B35C4A] hover:bg-[#9E4E3E] transition text-white"
            data-testid="cookie-consent-accept"
          >
            Accept all
          </button>
          <button
            type="button"
            onClick={() => { saveConsent({ analytics: false, marketing: false }); setMode(null); }}
            aria-label="Dismiss"
            className="p-2 opacity-70 hover:opacity-100 transition ml-auto sm:ml-0"
            data-testid="cookie-consent-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}
